import React, { useCallback } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import NCBISrrDropZone from './NCBISrrDropZone';
import NCBIUploadProgress from './NCBIUploadProgress';
import useNCBIUpload from './useNCBIUpload';

function NCBIUploadDialog({
  open,
  setOpen,
  activeProject,
  reloadRuns,
  setUserErrorMessage,
  setProgressMessage
}) {
  const handleSuccess = useCallback((result) => {
    reloadRuns?.();
    if ((result?.errors?.length || 0) === 0) {
      setOpen(false);
      setProgressMessage?.({
        status: 'completed',
        message: `Processed ${result?.totalCount || 0} NCBI identifiers successfully.`
      });
    }
  }, [reloadRuns, setOpen, setProgressMessage]);

  const handleError = useCallback((error) => {
    if (!error) {
      return;
    }

    let message = 'Failed to process NCBI identifiers. Please wait a few minutes and try again. Contact cepheid-informatics-support@cepheid.com if you require assistance.';
    if (error.code === 'EMPTY_FILE') {
      message = 'No SRR/ERR identifiers were found in the uploaded file. Please provide at least one identifier.';
    } else if (error.code === 'FILE_READ_ERROR') {
      message = 'The selected file could not be read. Please upload a valid .txt file.';
    }

    setUserErrorMessage?.(message);
  }, [setUserErrorMessage]);

  const {
    upload: uploadNCBIFile,
    progress,
    status,
    isUploading,
    uploadStartTime,
    completedCount,
    totalCount,
    results,
    clearResults
  } = useNCBIUpload({
    projectId: activeProject?.id,
    onSuccess: handleSuccess,
    onError: handleError
  });

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick' || isUploading) {
      return;
    }

    setOpen(false);
  };

  const handleFileSelected = useCallback((file) => {
    if (isUploading) {
      return;
    }

    uploadNCBIFile(file);
  }, [isUploading, uploadNCBIFile]);

  const handleCloseResults = useCallback(() => {
    clearResults();
    setOpen(false);
  }, [clearResults, setOpen]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Analyze from NCBI</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <NCBISrrDropZone
            onFileSelected={handleFileSelected}
            disabled={isUploading || !activeProject}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Upload a plain text file with one SRR/ERR identifier per line.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ mb: 1 }}>
        <Button color="primary" sx={{ mr: 2 }} onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
      </DialogActions>
      <NCBIUploadProgress
        open={isUploading}
        progress={progress}
        status={status}
        uploadStartTime={uploadStartTime}
        completedCount={completedCount}
        totalCount={totalCount}
        results={results}
        onClose={handleCloseResults}
      />
    </Dialog>
  );
}

export default NCBIUploadDialog;
